import { useRequest } from 'ahooks';
import { getTrades } from '../../api';
import { styled, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { Chains, statusText } from '../TradeList/TradeListColumn';
import ArrowDiagonal from '../icons/ArrowDiagonal';
import Link from 'next/link';
import { useAutoAnimate } from '@formkit/auto-animate/react';
import useFocus from './useFocus';
import classNames from 'classnames';
import { keyframes } from '@mui/system';
import { useTheme } from '@mui/material/styles';

const receivedAnimation = keyframes`
0%   {
	width: 0%;
}
100% {
	width: 100%;
}
`;

const AnimatedSpan = styled('span')<{ status: string }>(({ theme, status }) => ({
	background: theme.palette.common.backgroundSecondary,
	height: '100%',
	position: 'absolute',
	left: 0,
	zIndex: 0,
	animation: status === 'Completed' ? `${receivedAnimation} 300ms` : 'none',
}));

function Row({ item }: { item: TradeListItemResponse }) {
	const timeString = useMemo(() => moment(item.initiatedAt).fromNow(), [item.initiatedAt]);
	const status = useMemo(() => statusText(item), [item]);
	const theme = useTheme();

	const inProgressAnimation = keyframes`
0%   {
	background-color: ${theme.palette.common.backgroundPrimary};
	border-image-source: ${theme.palette.common.colorfulBorderVertical};
	border-image-width: 0 0 0 3px;
	border-image-slice: 1;
}
90% {
	background-color: ${theme.palette.common.backgroundSecondary};
	border-image-source: ${theme.palette.common.colorfulBorderVertical};
	border-image-width: 0 0 0 3px;
	border-image-slice: 1;
}
100%   {
	background-color: ${theme.palette.common.backgroundPrimary};
	border-image-source: none;
	border-image-width: 0;
	border-image-slice: 1;
}
`;
	const receivedTextAnimation = keyframes`
0%   {
	background: ${theme.palette.common.colorfulBorder};
	background-clip: text;
	-webkit-text-fill-color: transparent;
}
100% {
	background: ${theme.palette.common.colorfulBorder}; 
	background-clip: text;
	-webkit-text-fill-color: transparent;
}
`;

	return (
		<Box
			sx={{
				cursor: 'pointer',
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'stretch',
				justifyContent: 'space-around',
				width: '100%',
				gap: 1,
				p: 3,
				borderTop: '1px solid',
				color: 'common.textSecondary',
				borderColor: 'common.borderPrimary',
				transition: 'background-color color',
				textDecoration: 'none',
				transitionDuration: '300ms',
				position: 'relative',
				animation: status === "In progress" ? `${inProgressAnimation} 3s` : undefined,
				'& .chains-chained': {
					transition: 'filter',
					transitionDuration: '300ms',
					filter: 'grayscale(1)',
				},
				':hover': {
					backgroundColor: 'common.backgroundSecondary',
					color: 'common.textPrimary',
					'& .chains-chained': {
						filter: 'grayscale(0)',
					},
				},
			}}
			component={Link}
			href={`tx/${item.orderId}`}
		>
			<AnimatedSpan status={status} />
			<Typography variant='appSmallRegular' color='common.textSecondary' sx={{ zIndex: 1 }}>
				{timeString}
			</Typography>
			<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', zIndex: 1 }}>
				<Typography variant='appSmallBold' color={status === "In progress" ? 'common.textPrimary' : 'common.textSecondary'} sx={{ animation: status === "Completed" ? `${receivedTextAnimation} 1s` : undefined }}>{status}</Typography>
				<Box className='chains-chained'>
					<Chains
						chain1={item.sourceChain}
						chain2={item.service === 'WH_BRIDGE' ? item.sourceChain : item.swapChain}
						chain3={item.destChain}
						small
					/>
				</Box>
			</Box>
		</Box>
	);
}

export default function RecenTrades() {
	//const [i, seti] = useState(0);
	const [parent] = useAutoAnimate();
	const windowActive = useFocus();
	const [results, setResults] = useState<TradeListItemResponse[]>([])
	useRequest(() => getTrades({ perPage: 7, page: 0 }), {
		pollingInterval: windowActive ? 750 : undefined,
		pollingErrorRetryCount: 1,
		refreshOnWindowFocus: true,
		onSuccess(data) {
			let sorted = data.data.sort((a, b) => {
				let aIdx = results.findIndex(res => res.id === a.id),
					bIdx = results.findIndex(res => res.id === b.id);

				if (aIdx === -1 && bIdx !== -1) {
					return -1;
				} else if (aIdx !== -1 && bIdx === -1) {
					return 1;
				} else if (aIdx !== -1 && bIdx !== -1) {
					return aIdx < bIdx ? -1 : 1;
				}
				return a.initiatedAt > b.initiatedAt ? -1 : 1;
			});
			//console.log(i, results, sorted);
			//seti(i + 1);
			setResults(sorted);
		}
	});


	return (
		<div className='h-full flex flex-col items-stretch user-select-none overflow-hidden'>
			<Link
				className={classNames(
					'h-12 flex flex-row items-center justify-between gap-2 px-6',
					'text-textPrimary transition-colors duration-300 ease-in-out hover:bg-backgroundSecondary',
					'hover:underline underline-offset-4',
				)}
				href='/transactions'
			>
				<Typography variant='appSmallBold'>Latest transactions</Typography>
				<ArrowDiagonal />
			</Link>
			<div className='flex-1 flex flex-col items-stretch' ref={parent}>
				{results?.map((item) => (
					<Row key={item.id} item={item} />
				))}
			</div>
		</div>
	);
}
